import { defaultTheme, RaThemeOptions } from 'react-admin';
import { common } from '@mui/material/colors';
import { deepmerge } from '@mui/utils';

// Source Serif Pro
import '@fontsource/source-serif-pro/400.css';
import '@fontsource/source-serif-pro/600.css';
import '@fontsource/source-serif-pro/700.css';

// Source Sans Pro
import '@fontsource/source-sans-pro/300.css';
import '@fontsource/source-sans-pro/400.css';
import '@fontsource/source-sans-pro/400-italic.css';
import '@fontsource/source-sans-pro/600.css';
import '@fontsource/source-sans-pro/700.css';

// Note that you don’t need to call createTheme yourself. React-admin will do it for you.

/**
 * The ToverView monospace stack (TVR-2155).
 *
 * Chosen: the system-UI mono stack, not a webfont and not bare 'Courier New'.
 *
 * Why:
 * - A bare `monospace` keyword resolves to whatever each browser happens to be configured with,
 *   so two surfaces rendering a hostname and an MQTT topic did not necessarily match each other.
 * - Bare 'Courier New' with no fallback is worse still: a machine without that face falls all the
 *   way back to the default *proportional* font and the column alignment the monospace exists for
 *   disappears entirely.
 * - A webfont would be the most consistent option, but it means a new @fontsource dependency,
 *   which AGENTS.md requires separate approval for. Not taken here.
 *
 * The stack below renders natively on every platform, adds no download weight, and its last entry
 * is the generic keyword, so it can never fall through to a proportional face.
 *
 * Read it as `theme.typography.fontFamilyMonospace`, or import this constant directly for use in
 * an `sx` prop. Do not re-declare the string at a call site.
 */
export const fontFamilyMonospace = "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace";

let theme = {
  ...defaultTheme,
  components: {
    ...defaultTheme.components,
  },
  palette: {
    ...defaultTheme.palette,
    primary: {
      main: '#f1be48',
    },
    secondary: {
      main: '#081f2c',
    },
    green: {
      main: '#4caf50',
    },
  },
  typography: {
    fontFamily: "'Source Sans Pro', sans-serif",
    // Single source of truth for monospace surfaces — see the comment on fontFamilyMonospace above.
    fontFamilyMonospace,
    fontWeightMedium: 600,
    h1: {
      fontFamily: "'Source Serif Pro', serif",
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontFamily: "'Source Serif Pro', serif",
      fontWeight: 700,
      fontSize: '2rem',
      letterSpacing: 0.25,
      lineHeight: 1.2,
    },
    h3: {
      fontFamily: "'Source Serif Pro', serif",
      fontWeight: 700,
      fontSize: '1.75rem',
      letterSpacing: 0.25,
      lineHeight: 1.2,
    },
    h4: {
      fontFamily: "'Source Serif Pro', serif",
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.2,
    },
    h5: {
      fontFamily: "'Source Serif Pro', serif",
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.2,
    },
    h6: {
      fontFamily: "'Source Serif Pro', serif",
      fontWeight: 600,
      fontSize: '1.25rem',
      letterSpacing: 0.15,
      lineHeight: 1.2,
    },
    subtitle1: {
      fontSize: '1.25rem',
      fontWeight: 300,
      letterSpacing: 0.15,
      lineHeight: 1.2,
    },
    subtitle2: {
      fontSize: '1.125rem',
      fontWeight: 300,
      letterSpacing: 0.2,
      lineHeight: 1.2,
    },
    body1: {
      fontSize: '1.125rem',
      letterSpacing: 0.25,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.8125rem',
      letterSpacing: 0.25,
      lineHeight: 1.5,
    },
    button: {
      fontSize: '1.125rem',
      fontWeight: 600,
      textTransform: 'none',
      lineHeight: 1.2,
    },
    caption: {
      fontSize: '0.8125rem',
      opacity: 0.6,
      letterSpacing: 0.4,
      lineHeight: 1.2,
    },
    overline: {
      fontSize: '0.688rem',
      opacity: 0.6,
      textTransform: 'uppercase',
      letterSpacing: 1,
      lineHeight: 1.2,
    },
  },
};

theme = deepmerge(theme, {
  components: {
    MuiButton: {
      defaultProps: {
        color: 'inherit',
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          'borderRadius': 30,
          'borderTopRightRadius': 10,
          'borderWidth': 2,
          'padding': '4px 16px',
          '&:hover': {
            borderWidth: 2,
          },
        },
        contained: {
          'border': '2px solid',
          '&.Mui-disabled': {
            borderColor: 'inherit',
          },
        },
        containedPrimary: {
          'borderColor': theme.palette.primary.main,
          '&:hover': {
            backgroundColor: 'white',
            borderColor: theme.palette.secondary.main,
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          'color': theme.palette.secondary.main,
          'fontWeight': 'bold',
          'textDecoration': 'underline',
          'textUnderlinePosition': 'under',
          '&:hover': {
            textDecorationThickness: 2,
          },
        },
      },
    },
    RaButton: {
      styleOverrides: {
        root: {
          color: theme.palette.secondary.main,
        },
      },
    },
    RaSingleFieldList: {
      styleOverrides: {
        root: {
          '& .RaSingleFieldList-link': {
            '& > *': {
              color: theme.palette.secondary.main,
            },
          },
        },
      },
    },
    RaReferenceField: {
      styleOverrides: {
        root: {
          '& .RaReferenceField-link': {
            '& > *': {
              'color': theme.palette.secondary.main,
              'fontWeight': 'bold',
              'textDecoration': 'underline',
              'textUnderlinePosition': 'under',
              '&:hover': {
                textDecorationThickness: 2,
              },
            },
          },
        },
      },
    },
    RaFilterForm: {
      styleOverrides: {
        root: {
          alignItems: 'center',
        },
      },
    },
    RaDatagrid: {
      styleOverrides: {
        root: {
          '& .RaDatagrid-expandedPanel': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        // MUI's default tooltip is a neutral grey that reads as off-brand against the rest of the
        // chrome. Reuse the existing secondary (#081f2c) rather than introducing a new hex.
        //
        // `common.white` is imported from @mui/material/colors rather than read as
        // `theme.palette.common.white`: this module-level `theme` is plain theme *options*, and
        // `palette.common` does not exist on it — react-admin's `defaultTheme.palette` contributes
        // only `background` and `secondary`, and MUI seeds `common` inside `createTheme()`, which
        // runs later. The import is the same token by identity: `createPalette` builds its output
        // from `common: { ...common }` off this very object.
        tooltip: {
          backgroundColor: theme.palette.secondary.main,
          color: common.white,
          fontSize: theme.typography.body2.fontSize,
          letterSpacing: theme.typography.body2.letterSpacing,
          lineHeight: theme.typography.body2.lineHeight,
        },
        // Arrows are opt-in per tooltip; colour it too so the ones that use it match the surface.
        arrow: {
          color: theme.palette.secondary.main,
        },
      },
    },
    RaSaveButton: {
      styleOverrides: {
        root: {
          'borderColor': theme.palette.primary.main,
          'backgroundColor': theme.palette.primary.main,
          'fontSize': theme.typography.body2.fontSize,
          'lineHeight': 1.5,
          '&.Mui-disabled': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
  },
});

export default theme as RaThemeOptions;
